/* eslint-disable import/no-cycle */
import React from 'react';
import PropTypes from 'prop-types';
import { useStore } from 'effector-react';
import { Flex, Box, Text, Button } from 'rebass';
import styled from 'styled-components';
import { t } from '@lingui/macro';

import { i18n } from '@lib/i18n';
import { IconButton } from '@oca/ui';
import { Send, CloseX, Attachment } from '@oca/icons';
import { useDropzone } from 'react-dropzone';
import useList from 'react-use/lib/useList';

import {
  $editingMessage,
  resetCommentText,
  updateCommentRequesting,
} from '../model/comment-flow';
import { RejectedFileList } from '../../../molecules';
import { CommentAttachments } from '../molecules/comment-attachments';

/**
 *
 * @param {{onSubmit: () => Promise }}
 */
export const CommentForm = ({
  id,
  onSubmit,
  submitStatus,
  onUpdate,
  ...props
}) => {
  const editingMessage = useStore($editingMessage);
  const { isLoading } = useStore(submitStatus);

  const [commentary, setCommentary] = React.useState('');
  const [rejected, { set: setRejected, remove: removeRejected }] = useList([]);
  const [
    attachments,
    { set: setAttachments, remove: removeAttachment },
  ] = useList([]);

  const inputRef = React.useRef(null);
  const { open, acceptedFiles, getInputProps } = useDropzone({
    onDropRejected: setRejected,
    maxSize: 50 * 1024 * 1024,
  });

  const handleDeleteAttachment = (val, idx) => removeAttachment(idx);
  const handleAttachmentUploading = React.useCallback(
    uploaded => {
      const filtered = uploaded.filter(i => !attachments.includes(i));

      if (filtered.length > 0) {
        setAttachments(attachments.concat(filtered));
      }
    },
    [attachments, setAttachments],
  );

  React.useEffect(() => {
    setCommentary('');
    resetCommentText();
  }, []);

  React.useEffect(() => {
    if (editingMessage) {
      setCommentary(editingMessage.text);
      setAttachments(editingMessage.attachmentFiles);
      inputRef.current.focus();
      inputRef.current.scrollIntoView({ behavior: 'smooth' });
    } else {
      setCommentary('');
      setAttachments([]);
    }
  }, [editingMessage, inputRef, setAttachments]);

  const handleCommentSubmit = async e => {
    e.preventDefault();
    const data = {
      text: commentary,
      ...(editingMessage
        ? {
            nextAttachments: attachments,
            prevAttachments: editingMessage.attachmentFiles,
          }
        : { attachmentFiles: attachments }),
    };
    (editingMessage
      ? updateCommentRequesting({ id: editingMessage.id, data })
      : onSubmit({ id, data })
    )
      .finally(() => {
        setCommentary('');
        setAttachments([]);
        resetCommentText();
      })
      .catch(result => console.log('Unable to edit', result));
  };

  return (
    <FormWrap onSubmit={handleCommentSubmit} {...props}>
      <input type="text" {...getInputProps()} />
      <InputWrap>
        <TextInput
          name="text"
          value={commentary}
          placeholder={i18n._(t`Type your comment...`)}
          onChange={e => setCommentary(e.target.value)}
          autoComplete="off"
          ref={inputRef}
          required
        />
        <ButtonsWrapper>
          <AttachmentButton onClick={open}>
            <Attachment />
          </AttachmentButton>
          <SendButton
            type="submit"
            disabled={
              (commentary === '' && attachments.length === 0) || isLoading
            }
          >
            <Send color="#fff" />
          </SendButton>
        </ButtonsWrapper>
      </InputWrap>
      {rejected && rejected.length > 0 && (
        <RejectedFileList
          items={rejected}
          onDelete={(name, idx) => removeRejected(idx)}
          validation={{ maxSize: 50 * 1024 * 1024 }}
        />
      )}
      <CommentAttachments
        accepted={acceptedFiles}
        list={attachments}
        onDelete={handleDeleteAttachment}
        onUpload={handleAttachmentUploading}
      />
    </FormWrap>
  );
};
CommentForm.propTypes = {
  id: PropTypes.number.isRequired,
  onSubmit: PropTypes.func.isRequired,
  submitStatus: PropTypes.shape({
    id: PropTypes.string,
  }).isRequired,
  // eslint-disable-next-line react/require-default-props
  onUpdate: PropTypes.func,
};

const FormWrap = styled.form`
  width: 100%;
  flex: 1;
`;

const InputWrap = styled(Box)`
  flex: 1;
  position: relative;
`;

const TextInput = styled.textarea`
  width: 100%;
  min-height: 80px;
  resize: none;
  border: 1px solid ${({ theme }) => theme.colors.stroke};
  background: ${({ theme }) => theme.colors.secondary};
  border-radius: 12px;
  font-size: 15px;
  color: #101828;
  padding: 14px 52px 44px 14px;
  outline: none;
  font-weight: 400;
  line-height: 1.4;

  &::placeholder {
    color: ${({ theme }) => theme.colors.textSecondary};
  }
`;

const ButtonsWrapper = styled.div`
  position: absolute;
  bottom: 8px;
  right: 10px;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const AttachmentButton = styled(Button)`
  padding: 6px;
  background: transparent;

  svg {
    width: 20px;
    height: 20px;
    color: ${({ theme }) => theme.colors.textSecondary};
    transform: rotate(-45deg);
  }
`;

const SendButton = styled(Button)`
  background-color: ${({ theme }) => theme.colors.primary.main};
  border-radius: 50%;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  svg {
    color: #fff;
    width: 18px;
    height: 18px;
  }
`;

const EditingContainer = styled(Flex)`
  position: absolute;
  top: 0;
  left: 0;
  transform: translateY(calc(-100% - 6px));
  width: 100%;
  background-color: ${({ theme }) => theme.colors.primary.main};
  border-radius: 8px;
  padding: 6px 8px;
  align-items: center;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.1);
`;

const EditingMessage = styled(Text)`
  flex: 1;
  color: #fff;
  padding: 0 10px;
  font-size: 14px;
  line-height: 1.25;
`;
